/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Tag } from "@nordcloud/gnui";
import { usePeopleQuery } from "~/generated/graphql";
import { ContactSelector } from "~/components/Inputs/ContactSelector";
import { useDisclosure } from "~/hooks";
import { isNotNil } from "~/tools";

type Props = {
  contacts?: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  defaultNames: string[];
};

export function InlineContacts({
  contacts,
  onAdd,
  onRemove,
  defaultNames,
}: Props) {
  const { isOpen, close, open } = useDisclosure();
  const { data, loading } = usePeopleQuery();

  const filteredPeople = React.useMemo(
    () => (data?.people ?? []).filter(isNotNil),
    [data]
  );
  const peopleList = filteredPeople.filter(
    (person) => !contacts?.includes(person.id)
  );

  return (
    <>
      {contacts?.map((contactId, index) => (
        <InlineAddContacts
          key={contactId}
          contactName={getContactName(
            filteredPeople,
            contactId,
            defaultNames[index]
          )}
          onClick={() => onRemove(contactId)}
        />
      ))}
      <Tag text="Add" icon="plus" onClick={open} />
      <ContactSelector
        isOpen={isOpen}
        onClose={close}
        onSubmit={(personId) => {
          onAdd(personId);
          close();
        }}
        options={peopleList}
        peopleLoading={loading}
      />
    </>
  );
}

type AddContactProps = {
  contactName: string;
  onClick: () => void;
};

function InlineAddContacts({ contactName, onClick }: AddContactProps) {
  return (
    <Tag text={contactName} data-testid="add-cp" showClose onClick={onClick} />
  );
}

function getContactName(
  contacts: { id: string; name: string; email: string }[],
  contactId: string,
  fallbackName: string
) {
  return (
    contacts.find((contact) => contact.id === contactId)?.name ?? fallbackName
  );
}
